import styled from "@emotion/styled";
import { fonts, breakpoints } from "@trustpilot/businessapp-patternlibrary";

import { zIndexValues } from "../constants";

export const StyledBusinessAccountSelector = styled.div({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  margin: 0,
  overflow: "hidden",
  width: "100%",
  borderBottom: `solid 1px var(--elevation-surface-alt)`,
  backgroundColor: "var(--elevation-background)",
  [`@media (min-width: ${breakpoints.medium}px)`]: {
    height: 60,
    ["&:hover"]: {
      backgroundColor: "var(--elevation-surface-alt)",
    },
  },
});

interface StyledBusinessUnitDropdownProps {
  active: boolean;
}

export const StyledBusinessUnitDropdown = styled.div<StyledBusinessUnitDropdownProps>({
  backgroundColor: "var(--action-primary-background)",
  display: "block",
});

export const StyledBusinessUnitDropdownList = styled.ul({
  listStyle: "none",
  margin: "0",
  padding: "0",
  overflow: "auto",
  [`@media (min-width: ${breakpoints.medium}px)`]: {
    maxHeight: "calc(100vh - 200px)",
  },
  scrollbarWidth: "none",
});

interface StyledBusinessUnitDropdownItemProps {
  focused: boolean;
  selected: boolean;
}

export const StyledBusinessUnitDropdownItem = styled.li<StyledBusinessUnitDropdownItemProps>({
  ...fonts["body-2"],
  alignItems: "center",
  color: `var(--action-primary-text)`,
  cursor: "pointer",
  display: "flex",
  padding: "8px 16px 8px 12px",
  whiteSpace: "nowrap",

  ["&:hover"]: {
    background: "var(--form-selected)",
  },

  [`@media (min-width: ${breakpoints.medium}px)`]: {
    maxWidth: "390px",
  },
});

const BaseIcon = styled.i({
  flexShrink: 0,
  display: "flex",
});

export const StyledCheckIcon = styled(BaseIcon)({
  alignItems: "center",
  margin: "0 14px 0 0",
  minWidth: "16px",
  svg: {
    fill: "var(--text-action-primary)",
  },
});

export const StyledBusinessUnitDropdownItemWrapper = styled.span({
  margin: "0 8px 0 0",
  textOverflow: "ellipsis",
  overflow: "hidden",
});

export const StyledLockIcon = styled(BaseIcon)({
  alignItems: "center",
  margin: "0 0 0 auto",
});

export const StyledBusinessUnitSelector = styled.div({
  background: "var(--neutral-gray-100)",
  position: "absolute",
  top: "100%",
  left: "12px",
  zIndex: zIndexValues.z80,
  fontVariantLigatures: "no-common-ligatures",
  boxShadow: "0px 0px 8px rgb(0 0 0 / 30%)",
  borderRadius: "var(--border-radius-l)",
  overflow: "hidden",
  width: "calc(100% - 24px)",

  [`@media (min-width: ${breakpoints.small}px)`]: {
    borderRadius: "var(--border-radius-l)",
    left: "5px",
    top: "calc(100% - 8px)",
    width: "312px",
  },
});

export const StyledBusinessUnitSelectorWrapper = styled.div({
  maxHeight: "calc(100vh - 90px)",
  overflowY: "auto",
  scrollbarColor: `var(--neutral-stone-98) var(--neutral-stone-98)`,
  scrollbarWidth: "none",

  "::-webkit-scrollbar-track": {
    background: "var(--neutral-stone-98)",
    borderRadius: 2,
  },
  "::-webkit-scrollbar-thumb": {
    background: "var(--neutral-stone-98)",
    borderRadius: 2,
  },
  "::-webkit-scrollbar-thumb:hover": {
    background: "var(--neutral-stone-40)",
  },
});

export const StyledSelectedItemCardSwitch = styled.span({
  color: `var(--action-primary-text)`,
  display: "block",
  fontSize: "14px",
  width: "100%",
  lineHeight: "20px",
});

export const StyledSelectedItemCardArrowRight = styled(BaseIcon)({
  padding: "0 0 0",
  margin: "0 0 0 auto",
});

export const StyledSelectedItemCardInfoWrapper = styled.div({
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
});

export const StyledSelectedItemCardInfoName = styled.div({
  ...fonts["body-1"],
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  color: `var(--action-primary-text)`,
  marginBottom: 2,
});

const SharedCard = {
  display: "flex",
  padding: "16px 20px",
  cursor: "pointer",
  color: `var(--action-primary-text)`,
  fill: "currentColor",
  alignItems: "center",

  "&:hover": {
    background: "var(--form-selected)",
    //https://github.com/emotion-js/emotion/issues/1275
    [StyledSelectedItemCardSwitch as any]: {
      color: "var(--action-secondary-hover-text)",
    },
    //https://github.com/emotion-js/emotion/issues/1275
    [StyledSelectedItemCardArrowRight as any]: {
      color: "var(--action-secondary-hover-text)",
    },
    [StyledSelectedItemCardInfoWrapper as any]: {
      [StyledSelectedItemCardInfoName as any]: {
        color: "var(--action-secondary-hover-text)",
      },
    },
  },

  [`@media (min-width: ${breakpoints.medium}px)`]: {
    padding: "12px",
  },
};

export const StyledItemSelector = styled.div({
  position: "relative",
  background: `var(--action-primary-background)`,
  borderBottom: `solid 1px var(--divider-subtle)`,
  overflow: "hidden",

  "a:hover": {
    background: "var(--form-selected)",
    color: "var(--action-hover-text)",
  },

  "&:last-of-type": {
    borderBottom: "none",
    borderBottomLeftRadius: "16px",
    borderBottomRightRadius: "16px",
    overflow: "hidden",
  },

  "&:first-of-type": {
    borderTopLeftRadius: "16px",
    borderTopRightRadius: "16px",
    overflow: "hidden",
  },

  [`@media (min-width: ${breakpoints.medium}px)`]: {
    "&:hover": {
      //https://github.com/emotion-js/emotion/issues/1275
      [StyledBusinessUnitDropdown as any]: {
        display: "block",
      },
    },
  },
});

export const StyledSelectedItemCard = styled.div(SharedCard);

export const StyledSelectedItemCardIcon = styled(BaseIcon)({
  margin: "4px 16px 0 0",
  [`@media (min-width: ${breakpoints.medium}px)`]: {
    margin: "4px 12px 0 0",
  },
});

export const StyledSelectedItemCardInfo = styled.div({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  fontSize: "16px",
  width: "100%",
  overflow: "hidden",
  margin: "0 8px 0 0",
});

export const StyledSelectedItemCardLock = styled(BaseIcon)({
  padding: "4px 0 0",
  margin: "0 8px 0 0",
});

interface StyledProfileLinkProps {
  isExternal?: boolean;
}

export const StyledProfileLink = styled.a<StyledProfileLinkProps>({
  ...SharedCard,
  ...fonts["body-2"],
  display: "flex",
  alignItems: "center",
});

export const StyledProfileIcon = styled(BaseIcon)({
  alignItems: "center",
  margin: "0 16px 0 0",
});

export const StyledExternalLinkIcon = styled(BaseIcon)({
  alignItems: "center",
  margin: "0 0 0 8px",
  width: 10,
  height: 10,
  ".trustpilot-icon": {
    fill: "var(--text-action-primary)",
    "&:hover": {
      fill: "var(--text-action-primary)",
    },
  },
});

export const StyledSelectorButton = styled.div({
  alignItems: "center",
  display: "inline-flex",
  color: "var(--text-default)",
  fill: "currentColor",
  cursor: "pointer",
  height: 60,
  width: "100%",
  marginRight: 24,

  "@media (max-width: 490px)": {
    maxWidth: "43vw",
  },

  [`@media (max-width: ${breakpoints.medium}px)`]: {
    paddingRight: 12,
  },

  [`@media (min-width: ${breakpoints.medium}px)`]: {
    alignItems: "stretch",
    display: "flex",
    margin: 0,
    padding: "0 20px 0 20px",
  },

  [`@media (min-width: ${breakpoints.small}px)`]: {
    alignItems: "center",
    height: 65,
  },
});

export const StyledBurgerIcon = styled.span({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 40,
  width: 40,
  margin: "0 12px 0 0",
  background: "var(--action-primary-background)",
  borderRadius: "var(--border-radius-full)",
  fill: "var(--action-primary-text)",
  flexShrink: 0,

  [`@media (min-width: ${breakpoints.medium}px)`]: {
    display: "none",
  },
});

export const SelectorButtonInfo = styled.div({
  margin: "0 24px 0 0",
  overflow: "hidden",
  whiteSpace: "nowrap",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  minWidth: "86px",
});

const SelectorButtonInfoText = styled.div({
  overflow: "hidden",
  textOverflow: "ellipsis",
});

export const SelectorButtonInfoCompany = styled(SelectorButtonInfoText)({
  fontSize: "11px",
  opacity: "80%",
  lineHeight: "16px",
  fontWeight: fonts.weights.medium,
});

export const SelectorButtonInfoDomain = styled(SelectorButtonInfoText)({
  display: "flex",
  flexWrap: "nowrap",
});

export const SelectorButtonDomainPart = styled.span({
  flex: "0 auto",
  overflow: "hidden",
  textAlign: "center",
  textOverflow: "ellipsis",
  lineHeight: "20px",
});

export const SelectorButtonDomainSuffix = styled.span({
  flex: "initial",
  lineHeight: "20px",
});

export const SelectorButtonLockIcon = styled(BaseIcon)({
  alignItems: "center",
  margin: "0 4px 0 0",
});

export const StyledArrowDownIcon = styled(BaseIcon)({
  alignItems: "center",
  [`@media (min-width: ${breakpoints.small}px)`]: {
    margin: "0 0 0 auto",
  },
});

export const SearchInputWrapper = styled.div({
  padding: "0 10px 13px 13px",
  marginTop: "8px",

  'input[class*="-SearchInput"]': {
    height: "32px",
  },

  'input[type="search"]': {
    paddingRight: "16px",
  },
});

export const StyledToggle = styled.div({
  left: "6px",
  position: "relative",
  alignItems: "center",
  display: "flex",
  height: "58px",
  justifyContent: "center",
  margin: "-22px 0 0 0",
  width: "58px",

  [`@media (min-width: ${breakpoints.small}px)`]: {
    height: "65px",
    margin: 0,
  },

  [`@media (min-width: ${breakpoints.medium}px)`]: {
    height: "58px",
    margin: "-22px 0 0 0",
  },
});

export const StyledArrowDown = styled.div((props: { isOpen }) => ({
  transition: "height .3s ease-in-out",
  height: props.isOpen ? "auto" : "0",
}));

export const StyledSsoIcon = styled.span({
  border: "solid 1px var(--action-primary-text)",
  borderRadius: "var(--border-radius-s)",
  fontSize: "var(--s-body-xs-font-size)",
  padding: "1px 2px",
  lineHeight: "normal",
});
